import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Auth, user } from '@angular/fire/auth';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Network } from '@capacitor/network';

import { environment } from '../../environments/environment';
import { AppStatusStore } from './app-status.store';
import {
  CHECK_EVERY_MS, CURRENT_KEY, LAST_USED_KEY, RUNTIME_KEY, SHELL_KEY, UNAVAILABLE_KEY, UNAVAILABLE_RETRY_MS,
  UPDATED_KEY, UPDATED_NOTE, CheckPlan, ShellIdentity,
  clearsTyping, copyNeededForUpdate, isSafeMoment, nextHandoff, planCheck, readShell, resolverUrl, serverAnswers, stagedKey, wantsSoftUpdates,
  webStopWatch, webStopVerdict, WEB_CHECK_EVERY_MS, RESOLVER, mustStopOffline,
} from './app-update';
// Was (2026-10-15): import { storedCopyFileFor } from './offline-copy';
import { StoredContent, contentFileName, fetchCopyFile, getOfflineCopy, handOverCopyPointer, startOfflineCopyDownload, storedCopyFileFor } from './offline-copy';
import { setAppStopped } from './app-stopped';
import { onSaved } from './save-signal';

/** How long to WAIT on an engine download before giving up on this attempt. Deliberately longer than
 *  CHECK_EVERY_MS: the download itself keeps running and is reused (see `inFlight`), so a slow phone
 *  finishes one download rather than restarting it every check. */
const STAGE_TIMEOUT_MS = 1_800_000;

function withDeadline<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${what} timed out`)), ms);
    p.then((v) => { clearTimeout(timer); resolve(v); }, (e) => { clearTimeout(timer); reject(e); });
  });
}

// Was (2026-10-15): type Staged =
// Was (2026-10-15):   | { key: string; kind: 'engine'; version: string; dir: string; handoff: any }
// Was (2026-10-15):   | { key: string; kind: 'content'; handoff: any };
/** `copy`: the new version's Offline copy, already on the phone, that the pointer moves to on apply. */
type Staged =
  | { key: string; kind: 'engine'; version: string; dir: string; handoff: any; copy: StoredContent | null }
  | { key: string; kind: 'content'; handoff: any; copy: StoredContent | null };

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('could not load ' + src));
    document.head.appendChild(s);
  });
}

/** The shell's own downloader. The engine build ships public/shell-gate/*, so the same verified,
 *  atomic ZavoBundles.ensure the shell uses is one script tag away — no second implementation. */
async function shellBundles(): Promise<any> {
  const w = window as any;
  if (!w.fflate) await loadScript('/shell-gate/fflate.umd.js');
  if (!w.ZavoBundles?.ensure) await loadScript('/shell-gate/bundle-loader.js');
  if (!w.ZavoBundles?.ensure) throw new Error('ZavoBundles unavailable');
  return w.ZavoBundles;
}

/**
 * SOFT UPDATES (release Plan 7B). A Live phone app asks the resolver itself — at start, every 10 minutes,
 * on return to the front, and when the network comes back — downloads what changed in the background,
 * and applies it at a safe moment: the user returning to the app, or 30 s with no touch, nothing typed
 * on the page and no field focused. Unpublish stops the app at once; a too-old app says so.
 *
 * Also, for every runtime build: the online/offline and signed-in signals behind the banner, the
 * "Updated" note, and the last-used time the 7-day sign-in counts from.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private store = inject(AppStatusStore);
  private router = inject(Router);
  private auth = inject(Auth);

  private started = false;
  private shell: ShellIdentity | null = null;
  private staged: Staged | null = null;
  private checking = false;
  private staging = false;
  private lastTouch = Date.now();
  private typedOnPage = false;
  private unavailableTimer: ReturnType<typeof setInterval> | null = null;
  private checkTimer: ReturnType<typeof setInterval> | null = null;
  private applyTimer: ReturnType<typeof setInterval> | null = null;
  /** The engine download in progress, kept so a later check JOINS it instead of starting a second one.
   *  Two ensures for one version share `engines/.tmp-<v>`: the second's opening cleanup can delete what
   *  the first is still unpacking, and the first then renames a PARTIAL engine into place — which
   *  `exists()` makes permanent. One download per version, always. */
  private inFlight: { version: string; p: Promise<string> } | null = null;

  start(): void {
    if (this.started || !environment.isRuntime) return;
    this.started = true;

    this.watchNetwork();
    user(this.auth).subscribe((u) => this.store.signedIn.set(!!u));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.touchLastUsed();
    });

    this.shell = readShell(localStorage.getItem(SHELL_KEY));
    // Everything below — the update timers, staging, applying — is for a NATIVE Live app; a browser
    // takes new versions by reloading from the CDN and has no engine to swap. But being WITHDRAWN
    // applies to both, so a Live page in a browser watches for that on its own (watchWebStop).
    if (!wantsSoftUpdates(this.shell, Capacitor.isNativePlatform(), environment.isRuntime)) {
      this.watchWebStop();
      return;
    }

    this.afterUpdate();
    this.watchActivity();
    // Was (2026-10-15): this.store.onRetry = () => void this.check();
    // Try again on the offline screen reopens the app; everywhere else it asks the resolver.
    this.store.onRetry = () => { if (this.store.stopped() === 'offline') this.reopen(); else void this.check(); };
    void this.guardOfflineOpen();
    App.addListener('appStateChange', ({ isActive }) => { if (isActive) void this.onResume(); })
      .catch(() => { /* no App plugin: the 10-minute check still runs */ });
    this.checkTimer = setInterval(() => void this.check(), CHECK_EVERY_MS);
    this.applyTimer = setInterval(() => this.applyIfSafe(), 5_000);
    void this.check();
  }

  /** Ask the resolver once and act on the answer. Never throws. */
  async check(): Promise<void> {
    if (!this.shell || this.checking) return;
    let plan: CheckPlan;
    let wasStopped = false;
    // THE GUARD COVERS THE QUESTION, NOT THE DOWNLOAD. Holding it across a multi-megabyte engine
    // download would let one stalled download wedge every later check — the 10-minute timer, the
    // resume check, the network-return check and Try again — so the app could no longer be updated,
    // blocked or stopped until a cold start.
    this.checking = true;
    try {
      let status = 0;
      let body: any = null;
      try {
        const r = await fetch(resolverUrl(this.shell, localStorage.getItem(CURRENT_KEY)), { cache: 'no-store' });
        status = r.status;
        body = await r.json().catch(() => null);
      } catch { status = 0; }

      plan = planCheck(status, body, (window as any).ZAVO_RUNTIME?.version);
      if (plan.kind === 'retry') return;
      // The offline screen has nothing behind it — the app never drew — so a real answer reopens the
      // app from the top instead of lifting the screen off a blank page. It comes before the unpublish
      // answer: a 410 arriving under the offline screen must not replace it, or a later republish would
      // lift the screen over an app that never loaded. The fresh boot judges instead.
      if (this.store.stopped() === 'offline') { this.reopen(); return; }
      if (plan.kind === 'unavailable') { this.stopUnavailable(); return; }
      // Was (2026-10-15): moved above the unavailable branch (the offline reopen sat here, after it).

      // Any real answer: the app is published, and a password change / disable reaches the phone.
      // A FORCED token refresh, not reload(): a revoked refresh token (the password changed) is only
      // discovered when the token is actually refreshed, which otherwise waits up to an hour.
      localStorage.removeItem(UNAVAILABLE_KEY);
      this.auth.currentUser?.getIdToken(true).catch(() => { /* the SDK signs out on disabled / revoked */ });
      // The running version's copy, if an earlier download failed or never started: tried again on
      // every real answer, so a phone that missed it once still gets one.
      if (this.auth.currentUser) startOfflineCopyDownload(() => this.idToken(), true);

      if (plan.kind === 'blocked') { this.store.stopped.set('blocked'); setAppStopped(true); return; }
      // A STOP IS CLEARED BY ANY ANSWER THAT PROVES THE APP IS PUBLISHED — not only 'none'.
      // unstop() is the only thing that allows writes again and restarts the check and apply
      // timers, so gating it on 'none' left one case dead: unpublish, then republish WITH a new
      // version or engine. That answer is 'engine'/'content', so the app stayed stopped — every
      // write refused on a live app, with no timer left to heal it — until the user backgrounded
      // the app and came back. Clear it here, BEFORE staging, and the very same check recovers.
      wasStopped = this.store.stopped() !== null;
      if (wasStopped) this.unstop();
      if (plan.kind === 'none') return;
    } finally {
      this.checking = false;
    }

    if (!this.staging) {                           // a download already running is JOINED, not restarted
      this.staging = true;
      try { await this.stage(plan); } finally { this.staging = false; }
    }
    // A STOPPED APP HAD NOTHING ON SCREEN TO LOSE — but it is not stopped any more. `unstop()` above
    // gave the user their app back, and staging an engine can take minutes, so by the time this line
    // is reached they may be halfway through a form. Go through the safe-moment check like any other
    // update rather than reloading under them; a republish is not an emergency.
    if (wasStopped && this.staged) this.applyIfSafe();
  }

  private async stage(plan: Extract<CheckPlan, { kind: 'engine' | 'content' }>): Promise<void> {
    const key = stagedKey(plan);
    if (this.staged?.key === key) return;
    try {
      // Was (2026-10-15): // The Offline copy for the new version is NOT fetched here: after the swap the app is online and
      // Was (2026-10-15): // signed in, and AuthService's existing after-sign-in download fetches it for the next open.
      // Was (2026-10-15): const handoff = nextHandoff(plan.project, plan.content, storedCopyFileFor(plan.content));
      // The new version's Offline copy comes FIRST and is required. Applying without it left a signed-in
      // phone running a version it could not open offline until a later online session fetched the copy.
      // A failure throws to the catch below: nothing is staged, and the next check tries again.
      const onPhone = storedCopyFileFor(plan.content);
      // Was (2026-10-15): const need = onPhone ? null : copyNeededForUpdate(plan.content, !!this.auth.currentUser);
      const need = onPhone ? null : copyNeededForUpdate(plan.content, !!this.auth.currentUser, (window as any).ZAVO_RUNTIME?.version);
      const copy = need ? await fetchCopyFile(need, () => this.idToken()) : null;
      const handoff = nextHandoff(plan.project, plan.content, copy ? contentFileName(copy.version) : onPhone);
      if (plan.kind === 'engine') {
        const B = await shellBundles();
        if (this.inFlight?.version !== plan.engine.version) {
          const p: Promise<string> = B.ensure(plan.engine);
          p.catch(() => { if (this.inFlight?.p === p) this.inFlight = null; });   // a failure may be retried
          this.inFlight = { version: plan.engine.version, p };
        }
        // A ceiling on WAITING, not on the download: giving up here frees the check loop, and the
        // next check joins the same download rather than starting a rival one.
        const dir: string = await withDeadline(this.inFlight.p, STAGE_TIMEOUT_MS, 'engine download');
        // Was (2026-10-15): this.staged = { key, kind: 'engine', version: plan.engine.version, dir, handoff };
        this.staged = { key, kind: 'engine', version: plan.engine.version, dir, handoff, copy };
      } else {
        // Was (2026-10-15): this.staged = { key, kind: 'content', handoff };
        this.staged = { key, kind: 'content', handoff, copy };
      }
    } catch (e: any) {
      console.warn('[zavo] update not staged, will retry:', e?.message || e);
    }
  }

  private async apply(): Promise<void> {
    const s = this.staged;
    if (!s) return;
    this.staged = null;
    try {
      // The pointer moves WITH the handoff: from here on the phone opens the new version from its own copy.
      // Was (2026-10-15): if (s.copy) localStorage.setItem(CONTENT_KEY, JSON.stringify(s.copy));
      if (s.copy) handOverCopyPointer(s.copy);
      localStorage.setItem(RUNTIME_KEY, JSON.stringify(s.handoff));
      localStorage.setItem(UPDATED_KEY, '1');
      if (s.kind === 'engine') {
        const path: string = await (window as any).ZavoBundles.nativePathFor(s.dir);
        localStorage.setItem(CURRENT_KEY, s.version);
        await (window as any).Capacitor.Plugins.WebView.setServerBasePath({ path });   // reloads into it
        return;
      }
      window.location.reload();
    } catch (e: any) {
      localStorage.removeItem(UPDATED_KEY);
      console.warn('[zavo] update not applied:', e?.message || e);
    }
  }

  /** Owner Q1: leaving and returning is itself a safe moment. */
  private async onResume(): Promise<void> {
    this.touchLastUsed();
    this.typedOnPage = false;
    if (this.staged) { await this.apply(); return; }
    await this.check();
  }

  private applyIfSafe(): void {
    if (!this.staged) return;
    // A backgrounded app is not a safe moment, it is an absent one: applying here reloads a page the
    // user cannot see and loses what was on it. onResume applies instead, the moment they return.
    if (document.hidden) return;
    const el = document.activeElement as HTMLElement | null;
    const editing = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
    if (isSafeMoment(Date.now() - this.lastTouch, this.typedOnPage, editing)) void this.apply();
  }

  /**
   * "No unsaved form": typing marks the page busy until the user finishes, moves to another route,
   * or leaves the app. An ordinary button or link click does NOT clear it: it might be "add another
   * row", a tab, or a lookup, not the end of the form.
   *
   * "Finishes" is a SAVE, not a submit. No Zavo button is a submit — a button renders as a
   * `<div (click)>`, and no builder-authored page has a `<form>` — so clearsTyping and the submit
   * listener below can only ever fire on the editor's own forms. They are kept because they cost
   * nothing and are right the day a real form appears; the rule that actually fires inside an app
   * is the write itself (save-signal.ts). Without it, typing latched on the first keystroke and a
   * staged Soft update could only be applied by leaving the app and coming back.
   */
  private watchActivity(): void {
    const touch = () => { this.lastTouch = Date.now(); };
    for (const ev of ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll']) {
      document.addEventListener(ev, touch, { capture: true, passive: true });
    }
    document.addEventListener('input', () => { this.typedOnPage = true; touch(); }, { capture: true });
    document.addEventListener('click', (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (clearsTyping({ tagName: t.tagName, type: (t as HTMLButtonElement).type, closestSubmit: !!t.closest?.('[type="submit"]') })) {
        this.typedOnPage = false;
      }
    }, { capture: true });
    // A form that actually submits is the clearest "finished" there is — where one exists.
    document.addEventListener('submit', () => { this.typedOnPage = false; }, { capture: true });
    // What a Zavo app really has: a successful write. The work the user was typing is stored, so
    // the page is no longer unsaved and a staged update may apply at the next quiet moment.
    // A SAVED WRITE MEANS "FINISHED" ONLY IF THEY HAVE STOPPED TYPING. The signal is "a mutation
    // succeeded", which is usually the user's form being saved — but a page that writes while they
    // are still filling it in ("add another row") would otherwise report them finished mid-form,
    // the very case this rule exists to protect. A cursor still in a field says they are not.
    onSaved(() => {
      const el = document.activeElement as HTMLElement | null;
      const editing = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
      if (!editing) this.typedOnPage = false;
    });
    this.router.events.subscribe((e) => { if (e instanceof NavigationEnd) this.typedOnPage = false; });
  }

  /**
   * A Live app open in a BROWSER, watching for its own withdrawal.
   *
   * The phone has the whole soft-update machine; the web needs one fact — is this app still
   * published — because a builder who unpublishes expects it gone from every tab, not just from new
   * visitors. Reads keep working (the page is already drawn); writes are refused, exactly as on the
   * phone, and the stop screen says the same words. Rechecks on the same 10-minute cadence, and
   * whenever the tab comes back to the front.
   */
  private watchWebStop(): void {
    const w = webStopWatch((window as any).ZAVO_RUNTIME, Capacitor.isNativePlatform(), environment.isRuntime);
    if (!w.watch) return;
    const ask = async () => {
      let status = 0;
      try {
        const r = await fetch(`${RESOLVER}?projectId=${encodeURIComponent(w.projectId)}&channel=prod&shell=0`, { cache: 'no-store' });
        status = r.status;
      } catch { status = 0; }
      const verdict = webStopVerdict(status);
      if (verdict === 'unknown') return;                 // a blip must never take a live app down
      if (verdict === 'stop') {
        setAppStopped(true);
        this.store.stopped.set('unavailable');
        return;
      }
      setAppStopped(false);
      if (this.store.stopped() !== null) this.store.stopped.set(null);
    };
    setInterval(() => void ask(), WEB_CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void ask(); });
    void ask();
  }

  private watchNetwork(): void {
    Network.getStatus().then((s) => this.store.online.set(s.connected)).catch(() => {});
    Network.addListener('networkStatusChange', (s) => {
      this.store.online.set(s.connected);
      // Was (2026-10-15): if (s.connected) void this.check();
      if (s.connected && this.store.stopped() === 'offline') { this.reopen(); return; }
      if (s.connected) void this.check();
    }).catch(() => { /* no plugin: the banner simply never shows */ });
  }

  private stopUnavailable(): void {
    localStorage.setItem(UNAVAILABLE_KEY, '1');
    this.store.stopped.set('unavailable');
    // Stop meaning stop: no staging, no applying, no writes (app-stopped.ts). The unavailable
    // re-check is the ONLY timer left running — it is how the app comes back.
    setAppStopped(true);
    if (this.applyTimer) { clearInterval(this.applyTimer); this.applyTimer = null; }
    if (this.checkTimer) { clearInterval(this.checkTimer); this.checkTimer = null; }
    this.staged = null;
    if (!this.unavailableTimer) this.unavailableTimer = setInterval(() => void this.check(), UNAVAILABLE_RETRY_MS);
  }

  private unstop(): void {
    this.store.stopped.set(null);
    setAppStopped(false);
    if (!this.checkTimer) this.checkTimer = setInterval(() => void this.check(), CHECK_EVERY_MS);
    if (!this.applyTimer) this.applyTimer = setInterval(() => this.applyIfSafe(), 5_000);
    if (this.unavailableTimer) { clearInterval(this.unavailableTimer); this.unavailableTimer = null; }
  }

  /**
   * Signed in, no internet, and nothing on the phone to open: say so, instead of a white screen.
   * Judged once, at open — an app that loaded and then lost the network keeps what it drew.
   * Android's network flag alone is not trusted: on networks it has not validated it says offline while
   * requests go through, so the update server must also fail to answer.
   */
  private async guardOfflineOpen(): Promise<void> {
    try {
      await this.auth.authStateReady();
      // Was (2026-10-15): const online = await Network.getStatus().then((s) => s.connected, () => navigator.onLine);
      let online = await Network.getStatus().then((s) => s.connected, () => navigator.onLine);
      // Only probe when it matters (signed in, nothing to open), so an ordinary open never waits.
      if (!online && !!this.auth.currentUser && getOfflineCopy() === null) {
        online = await serverAnswers(resolverUrl(this.shell!, localStorage.getItem(CURRENT_KEY)));
      }
      if (mustStopOffline(!!this.auth.currentUser, getOfflineCopy() !== null, online)) this.store.stopped.set('offline');
    } catch { /* a failed judgement leaves the app as it was */ }
  }

  /** Back online after the offline screen: nothing was drawn, so open the app again from the top. */
  private reopen(): void {
    window.location.reload();
  }

  /** Just updated: say so once, and drop engines beyond the keep window (the quiet open never evicts). */
  private afterUpdate(): void {
    if (!localStorage.getItem(UPDATED_KEY)) return;
    localStorage.removeItem(UPDATED_KEY);
    this.store.showNote(UPDATED_NOTE);
    const current = localStorage.getItem(CURRENT_KEY);
    if (current) shellBundles().then((B) => B.evict(current)).catch(() => {});
  }

  private touchLastUsed(): void {
    try { localStorage.setItem(LAST_USED_KEY, String(Date.now())); } catch { /* full storage never blocks */ }
  }

  private idToken(): Promise<string | null> {
    const u = this.auth.currentUser;
    return u ? u.getIdToken() : Promise.resolve(null);
  }
}
